import IPC from '../../helpers/ipc';
import Logger from '../../../common/logger';
import { LAYERS } from '../layers/types';
import store from '../../helpers/store';
import { getConfigurationType } from '../../helpers/configuration/actions';
import { CONFIGURATION_TYPE } from '../../helpers/configuration/types';
import { MAIN_ACTIONS } from './types';
import { DEVICE, DISPLAY } from './types';
import * as WidgetActions from '../layers/widget/actions';
import { getWidgetDisplay } from '../layers/widget/helpers';
import * as PopupActions from '../layers/popup/actions';
import getRedirectUrl from './helpers/getRedirectUrl';
import DOM from '../../../common/DOM';

const getDevice = (size) => {
    const state = store.getState();

    if (size.width >= state.options.default.rwd.mediumBreakpoint) {
        return DEVICE.DESKTOP;
    } else if (
        size.width >= state.options.default.rwd.smallBreakpoint &&
        size.width < state.options.default.rwd.mediumBreakpoint
    ) {
        return DEVICE.MOBILE;
    }

    return DEVICE.NONE;
};

const updateMainIFrameMode = (mode) => {
    if (mode) {
        store.dispatch({
            type: MAIN_ACTIONS.SET_CHILD_IFRAME_MODE,
            data: {
                iFrameMode: mode
            }
        });
    }
};

const updateParentSize = (size) => {
    store.dispatch({
        type: MAIN_ACTIONS.UPDATE_PARENT_SIZE,
        data: {
            device: getDevice(size),
            parentWidth: size.width,
            parentHeight: size.height
        }
    });
};

const updateMainSize = (size) => {
    store.dispatch({
        type: MAIN_ACTIONS.UPDATE_MAIN_SIZE,
        data: {
            mainWidth: size.width,
            mainHeight: size.height
        }
    });
};

const updateIFrame = (size) => {
    const state = store.getState();

    IPC.sendEvent(MAIN_ACTIONS.SET_PARENT_IFRAME_MODE, {
        childWidth: size.width,
        childHeight: size.height,
        mode: state.main.iFrameMode
    });

    Logger.log('Size=' + JSON.stringify(size) + ', iFrameMode=' + state.main.iFrameMode);
};

export const updateMainLayer = (data) => {
    if (!data.layer) {
        return;
    }

    Logger.log(
        `updateMainLayer [layer=${data.layer}, iFrameMode=${data.iFrameMode}, source=${data.source}]`
    );

    const state = store.getState();
    let size = undefined;

    if (data.layer === LAYERS.WIDGET) {
        if (!state.main.isPopupLayerEnabled) {
            size = DOM.calculateElementSize(data.layer.toLowerCase());

            if (data.width) {
                size.width = data.width;
            }

            const iFrameMode = getWidgetDisplay();
            updateMainIFrameMode(iFrameMode);
            if (iFrameMode === DISPLAY.FULLWIDTH) {
                size.width = state.main.parentWidth;
            } else if (iFrameMode === DISPLAY.FULLSCREEN) {
                size.width = state.main.parentWidth;
                size.height = state.main.parentHeight;
            }
        }
    } else if (data.layer === LAYERS.POPUP) {
        size = {
            height: state.main.parentHeight,
            width: state.main.parentWidth
        };
        if (data.iFrameMode) {
            updateMainIFrameMode(data.iFrameMode);
        }
    }

    if (size) {
        size.width = size.width > state.main.parentWidth ? state.main.parentWidth : size.width;
        size.height = size.height > state.main.parentHeight ? state.main.parentHeight : size.height;
        updateMainSize(size);
        updateIFrame(size);
    }
};

export const onInit = () => {
    const state = store.getState();
    if (state.widget) {
        WidgetActions.onInit();
    }
    if (state.popup) {
        PopupActions.onInit();
    }
    store.dispatch({
        type: MAIN_ACTIONS.INIT,
        data: {
            parentWidth: state.main.parentWidth,
            parentHeight: state.main.parentHeight,
            device: state.main.device,
            iFrameMode: state.main.iFrameMode,
            location: state.main.location,
            isWidgetLayerEnabled: state.main.isWidgetLayerEnabled,
            isPopupLayerEnabled: state.main.isPopupLayerEnabled
        }
    });
};

export const onClose = ({ layer, reason }) => {
    if (layer === LAYERS.WIDGET) {
        store.dispatch({
            type: MAIN_ACTIONS.DISABLE_WIDGET_LAYER
        });
    } else if (layer === LAYERS.POPUP) {
        store.dispatch({
            type: MAIN_ACTIONS.DISABLE_POPUP_LAYER
        });
    }

    const state = store.getState();
    if (!state.main.isPopupLayerEnabled && !state.main.isWidgetLayerEnabled) {
        const size = {
            width: 0,
            height: 0
        };
        updateMainSize(size);
        updateIFrame(size);

        IPC.sendEvent(MAIN_ACTIONS.CLOSED, { reason });

        //TODO: reset all control stores

        const redirectUrl = getRedirectUrl(layer);
        if (redirectUrl) {
            setTimeout(() => {
                IPC.sendEvent(MAIN_ACTIONS.OPEN_URL, {
                    url: redirectUrl,
                    self: true
                });
            }, 700);
        }
    } else {
        updateMainLayer({
            layer: layer === LAYERS.POPUP ? LAYERS.WIDGET : LAYERS.POPUP
        });
    }
};

export const onOpen = (data) => {
    if (!data.config.defaultView) {
        Logger.error('Main.start: no defaultView');
    }

    try {
        const state = store.getState();
        if (
            state.main.isWidgetLayerEnabled &&
            state.widget.currentView === 'CMB' &&
            data.config.defaultView === 'cmbPopup'
        ) {
            Logger.log('CMB will be opened in popup layer, closing CMB in widget');
            onClose({ layer: LAYERS.WIDGET, reason: 'Opening CMB in popup' });
        } else if (
            state.main.isPopupLayerEnabled &&
            state.popup.currentView === 'CMB' &&
            data.config.defaultView === 'cmb'
        ) {
            Logger.log('CMB will not be opened in widget layer - it is open in popup');
            onClose({ layer: LAYERS.WIDGET, reason: 'CMB in popup' });
            return;
        }

        if (data.parentWidth && data.parentHeight && data.location) {
            store.dispatch({
                type: MAIN_ACTIONS.INIT,
                data: {
                    parentWidth: data.parentWidth,
                    parentHeight: data.parentHeight,
                    device:
                        data.parentWidth && data.parentHeight
                            ? getDevice({
                                  width: data.parentWidth,
                                  height: data.parentHeight
                              })
                            : undefined,
                    location: data.location
                }
            });
        }

        const viewType = getConfigurationType(data.config.defaultView);

        switch (viewType) {
            case CONFIGURATION_TYPE.WIDGET:
                WidgetActions.onOpen();
                store.dispatch({
                    type: MAIN_ACTIONS.ENABLE_WIDGET_LAYER
                });
                break;
            case CONFIGURATION_TYPE.POPUP:
                PopupActions.onOpen(data.config);
                store.dispatch({
                    type: MAIN_ACTIONS.ENABLE_POPUP_LAYER
                });
                break;
            case CONFIGURATION_TYPE.EIP:
                store.dispatch({
                    type: MAIN_ACTIONS.ENABLE_POPUP_LAYER
                });
                break;
            default:
                break;
        }

        IPC.sendEvent(MAIN_ACTIONS.OPEN);
    } catch (err) {
        IPC.sendEvent(MAIN_ACTIONS.CLOSED, { reason: 'Error onOpen: ' + err.message });
        Logger.error('MainActions.onOpen: ' + err.message, 'setup');
    }
};

export const onPreloaderClose = (configType) => {
    if (configType === CONFIGURATION_TYPE.WIDGET) {
        WidgetActions.onPreloaderClose();
    } else if (configType === CONFIGURATION_TYPE.POPUP) {
        PopupActions.onPreloaderClose();
    }
};

export const onParentResize = (size) => {
    updateParentSize(size);

    const state = store.getState();
    if (state.main.isWidgetLayerEnabled) {
        WidgetActions.onParentResize();
    }
    if (state.main.isPopupLayerEnabled) {
        PopupActions.onParentResize();
    }
};
